import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import {
    get_track_status,
    download_invoice,
    get_order_detail,
    refund_payment,
    get_order_audit_logs,
    seller_payment_hold,
    seller_payment_release,
    cancel_order,
    extend_shipment_deadline,
    get_shipping_methods,
    admin_create_shipment,
} from "../../reduxData/user/userAction";
import StatusTracker from "../../components/StatusTracker";
import { formatDateTime } from "../../hooks/formatDateTime";
import { formatPrice } from "../../hooks/formatPrice";
import { StatusText } from "../../components/StatusBadge";
import { useTranslation } from "react-i18next";
import TranslatedText from "../../hooks/TranslatedText";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import { Box } from "@mui/joy";
import Select from "react-select";

const getPurchasedVariants = (order) => {
    if (!order) return [];
    const orderedIds = order.ordered_variant_id || [];

    if (!orderedIds.length) {
        return (order.itemsdata || []).map(item => ({
            parentItem: item,
            variant: item.variantsData?.[0] || null,
        }));
    }

    return orderedIds.map(variantId => {
        const parentItem = order.itemsdata?.find(item =>
            item.variantsData?.some(v => v.id === variantId)
        );
        if (!parentItem) return null;
        const variant = parentItem.variantsData?.find(v => v.id === variantId);
        return { parentItem, variant };
    }).filter(Boolean);
};

const humanizeAuditValue = (value) => {
    if (!value) return "";
    return String(value)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, character => character.toUpperCase());
};

const getAuditTone = (log) => {
    return log?.status === true ? "success" : "danger";
};

const CANCEL_REASON_CODES = {
    SELLER_CANNOT_FULFILL: {
        code: "SELLER_CANNOT_FULFILL",
        label: "Seller cannot fulfil the order / stock error",
    },
    SELLER_MISSED_SHIP_DEADLINE: {
        code: "SELLER_MISSED_SHIP_DEADLINE",
        label: "Seller did not ship before the deadline",
    },
    FRAUD_COMPLIANCE_ISSUE: {
        code: "FRAUD_COMPLIANCE_ISSUE",
        label: "Fraud, compliance or product issue",
    },
    DUPLICATE_OR_SYSTEM_ERROR: {
        code: "DUPLICATE_OR_SYSTEM_ERROR",
        label: "Duplicate or incorrect order caused by a system error",
    },
};

const CANCEL_REFUND_OPTIONS = [
    {
        value: "payment",
        icon: "bi-credit-card",
        title: "Refund to original payment method",
        description: "Amount is returned to the buyer's card / bank account.",
    },
    {
        value: "store_credit",
        icon: "bi-wallet2",
        title: "Refund as store credit",
        description: "Amount is added to the buyer's store credit balance.",
    },
];

const cancelReasonOptions = Object.values(CANCEL_REASON_CODES).map((option) => ({
    value: option.code,
    label: option.label,
}));

const CancelSelectValueContainer = ({ children, innerProps, className }) => (
    <div {...innerProps} className={`${className || ""} cancel-select-value-wrap`}>
        <span className="cancel-order-icon-tile"><i className="bi bi-chat-square-text" /></span>
        {children}
    </div>
);

const MAX_EXTENSION_DAYS = 2;

const OrderDetails = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { accessToken } = useSelector((state) => state.auth);
    const { trackstatus, orderDetail, orderAuditLogs, totalOrderAuditLogs } = useSelector((state) => state.user);
    const [loading, setLoading] = useState(false);
    const [downloadType, setDownloadType] = useState("");
    const [refundConfirmation, setRefundConfirmation] = useState(false);
    const [releaseConfirmation, setReleaseConfirmation] = useState(false);
    const [reasonModal, setReasonModal] = useState(false);
    const [reason, setReason] = useState("");
    const [cancelOrderModal, setCancelOrderModal] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [cancelReasonCode, setCancelReasonCode] = useState("");
    const [refundMethod, setRefundMethod] = useState("store_credit");
    const [cancelLoading, setCancelLoading] = useState(false);

    const [extendDeadlineModal, setExtendDeadlineModal] = useState(false);
    const [extendReason, setExtendReason] = useState("");
    const [extensionWorkingDays, setExtensionWorkingDays] = useState("");
    const [extendLoading, setExtendLoading] = useState(false);

    const [createShipmentModal, setCreateShipmentModal] = useState(false);
    const [packageDetails, setPackageDetails] = useState({ length: "", width: "", height: "", weight: "" });
    const [packageDetailsErrors, setPackageDetailsErrors] = useState({});
    const [packageDetailsConfirmed, setPackageDetailsConfirmed] = useState(false);
    const [shippingMethods, setShippingMethods] = useState([]);
    const [shippingMethodsLoading, setShippingMethodsLoading] = useState(false);
    const [shippingMethodsLoaded, setShippingMethodsLoaded] = useState(false);
    const [selectedShippingMethod, setSelectedShippingMethod] = useState(null);
    const [adminSupportReason, setAdminSupportReason] = useState("");
    const [sellerConfirmedShipReady, setSellerConfirmedShipReady] = useState(false);
    const [shipmentLoading, setShipmentLoading] = useState(false);

    const { REACT_APP_IMAGE_URL, REACT_APP_REDIRECT_URL } = process.env;
    const { id: slugId } = useParams();

    const order = orderDetail?.[0] || orderDetail || {};

    const {
        order_id,
        order_status,
        amount,
        total_amount,
        order_created_at,
        delivery_date,
        payment_method,
        parcel_id,
        ship_by_at,
        is_return,
        ordered_variant_id,
        is_dispute,
        itemsdata = [],
        userdata = {},
        sellerdata = {},
        shipping_address = {},
        billing_address = {},
        seller_payment_status,
    } = order;

    const isReturn = is_return;
    const isReturned = order_status?.toLowerCase() === "returned";
    const isReturnOrder = isReturned && isReturn;

    const getFullImageUrl = (url) => {
        if (!url) return ""; // You can add a default image if needed
        return url.startsWith("http") || url.startsWith("https")
            ? url
            : `${REACT_APP_IMAGE_URL}${url}`;
    };

    useEffect(() => {
        if (!slugId || !accessToken) return;
        get_order_detail(dispatch, slugId);
        get_order_audit_logs(dispatch, slugId);
    }, [dispatch, accessToken, slugId]);

    const [width, setWidth] = useState(window.innerWidth);
    const isMobile = width <= 900;
    useEffect(() => {
        const h = () => setWidth(window.innerWidth);
        window.addEventListener("resize", h);
        return () => window.removeEventListener("resize", h);
    }, []);

    useEffect(() => {
        if (parcel_id) {
            get_track_status(dispatch, parcel_id);
        }
    }, [parcel_id, dispatch]);

    const downloadInvoice = async (type) => {
        try {
            setLoading(true);
            setDownloadType(type);
            const response = await download_invoice(dispatch, order_id, type);
            const blob = response.data;
            if (blob.size === 0) throw new Error("Empty file");
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${type}-invoice-${order_id}.pdf`;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            setDownloadType("");
            setLoading(false);
        }
    };

    const purchasedVariants = getPurchasedVariants(order);
    const isMultiItem = purchasedVariants.length > 1;
    const auditLogs = useMemo(() => {
        if (!Array.isArray(orderAuditLogs)) return [];
        return [...orderAuditLogs].sort((first, second) => {
            const firstTime = new Date(first?.created_at || 0).getTime();
            const secondTime = new Date(second?.created_at || 0).getTime();
            return firstTime - secondTime || Number(first?.id || 0) - Number(second?.id || 0);
        });
    }, [orderAuditLogs]);

    const handleContactEmail = () => {
        if (sellerdata?.email) window.location.href = `mailto:${sellerdata.email}`;
    };

    const handleContactPhone = () => {
        if (sellerdata?.phone_no) {
            window.location.href = `tel:${sellerdata.country_code}${sellerdata.phone_no}`;
        }
    };

    const handleDetail = (slug) => {
        if (slug) {
            window.open(
                `${REACT_APP_REDIRECT_URL}auction/${slug}`,
                "_blank",
                "noopener,noreferrer"
            );
        }
    };

    const handleRefund = async () => {
        const refunded = await refund_payment(dispatch, order_id);
        if (refunded) {
            setRefundConfirmation(false);
            await get_order_detail(dispatch, slugId);
        }
    };

    const holdPayout = async () => {
        const held = await seller_payment_hold(dispatch, {
            seller_id: sellerdata?.id,
            order_id,
            reason_code: reason,
            reason,
        });

        if (held) {
            setReason("");
            setReasonModal(false);
            await get_order_detail(dispatch, slugId);
        }
    };

    const releasePayout = async () => {
        const released = await seller_payment_release(dispatch, { order_id });
        if (released) {
            setReleaseConfirmation(false);
            await get_order_detail(dispatch, slugId);
        }
    };

    const canSubmitCancel = Boolean(cancelReason.trim() && cancelReasonCode && refundMethod);

    const closeCancelModal = () => {
        setCancelOrderModal(false);
        setCancelReason("");
        setCancelReasonCode("");
        setRefundMethod("store_credit");
    };

    const handleCancelOrder = async () => {
        const trimmedReason = cancelReason.trim();
        if (!trimmedReason || !order_id || !cancelReasonCode || !refundMethod) return;

        setCancelLoading(true);
        const cancelled = await cancel_order(dispatch, {
            order_id,
            reason: trimmedReason,
            reason_code: cancelReasonCode,
            refund_as_store_credit: refundMethod === "store_credit",
        });
        if (cancelled) {
            closeCancelModal();
            await Promise.all([
                get_order_detail(dispatch, slugId),
                get_order_audit_logs(dispatch, slugId),
            ]);
        }
        setCancelLoading(false);
    };

    const handleExtendDeadline = async () => {
        const trimmedReason = extendReason.trim();
        const days = Number(extensionWorkingDays);
        if (!trimmedReason || !order_id || !days || days <= 0 || days > MAX_EXTENSION_DAYS) return;

        setExtendLoading(true);
        const extended = await extend_shipment_deadline(dispatch, order_id, {
            reason: trimmedReason,
            extension_working_days: days,
        });
        if (extended) {
            setExtendDeadlineModal(false);
            setExtendReason("");
            setExtensionWorkingDays("");
            await Promise.all([
                get_order_detail(dispatch, slugId),
                get_order_audit_logs(dispatch, slugId),
            ]);
        }
        setExtendLoading(false);
    };

    const shippingPostalCode = shipping_address?.postal_code || billing_address?.postal_code;

    useEffect(() => {
        const fetchShippingMethods = async () => {
            if (!createShipmentModal || !packageDetailsConfirmed || !shippingPostalCode || !order_id) return;
            setShippingMethodsLoading(true);
            setShippingMethodsLoaded(false);
            try {
                const methods = await get_shipping_methods(dispatch, shippingPostalCode, order_id, {
                    weight_kg: packageDetails.weight,
                    length_cm: packageDetails.length,
                    width_cm: packageDetails.width,
                    height_cm: packageDetails.height,
                });
                setShippingMethods(Array.isArray(methods) ? methods : []);
                setShippingMethodsLoaded(true);
            } finally {
                setShippingMethodsLoading(false);
            }
        };
        fetchShippingMethods();
    }, [createShipmentModal, packageDetailsConfirmed, shippingPostalCode, order_id, dispatch]);

    const handlePackageDetailChange = (field, value) => {
        setPackageDetails((prev) => ({ ...prev, [field]: value }));
    };

    const handleConfirmPackageDetails = () => {
        const requiredFields = ["length", "width", "height", "weight"];
        const nextErrors = {};
        requiredFields.forEach((field) => {
            if (!String(packageDetails[field] ?? "").trim()) nextErrors[field] = true;
        });
        if (Object.keys(nextErrors).length > 0) {
            setPackageDetailsErrors(nextErrors);
            return;
        }
        setPackageDetailsErrors({});
        setPackageDetailsConfirmed(true);
    };

    const handleCloseCreateShipment = () => {
        if (shipmentLoading) return;
        setCreateShipmentModal(false);
        setPackageDetails({ length: "", width: "", height: "", weight: "" });
        setPackageDetailsErrors({});
        setPackageDetailsConfirmed(false);
        setShippingMethods([]);
        setShippingMethodsLoaded(false);
        setSelectedShippingMethod(null);
        setAdminSupportReason("");
        setSellerConfirmedShipReady(false);
    };

    const handleCreateShipment = async () => {
        const trimmedReason = adminSupportReason.trim();
        if (!selectedShippingMethod || !order_id || !trimmedReason) return;

        setShipmentLoading(true);
        const created = await admin_create_shipment(dispatch, {
            orderId: order_id,
            shipping_method_id: selectedShippingMethod.value,
            carrier: selectedShippingMethod.carrier,
            admin_support_reason: trimmedReason,
            seller_confirmed_ship_ready: sellerConfirmedShipReady,
        });
        if (created) {
            handleCloseCreateShipment();
            await Promise.all([
                get_order_detail(dispatch, slugId),
                get_order_audit_logs(dispatch, slugId),
            ]);
        }
        setShipmentLoading(false);
    };

    const shippingOptions = shippingMethods?.map((item) => ({
        value: item.id,
        label: item.name,
        carrier: item.carrier,
        minWeight: item.min_weight,
        maxWeight: item.max_weight,
    })) || [];

    const isCancelled = ["CANCELLED", "ORDER_CANCELLED"].includes(order_status);
    const isHiddenStatus = ["FAILED", "CANCELLED", "ORDER_CANCELLED", "PENDING", null].includes(order_status);
    const isDelivered = order_status?.toLowerCase() === "delivered";
    const canCancelOrder = Boolean(order_id)
        && !["delivered", "cancelled", "order_cancelled", "failed"].includes(String(order_status || "").toLowerCase());
    const isNeedsAdminReview = order_status === "NEEDS_ADMIN_REVIEW";
    const isShipByExpired = Boolean(ship_by_at) && new Date(ship_by_at).getTime() <= Date.now();
    const showAdminReviewActions = canCancelOrder && isNeedsAdminReview && isShipByExpired;
    const isPayoutOnHold = seller_payment_status?.toLowerCase() === "hold";

    const addressCards = (
        <div className="od2-address-row">
                        <div className="od2-card">
                            <h4 className="od2-card-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                {t("order.BillingAddress")}
                            </h4>
                            <div className="od2-address-block">
                                <p>{billing_address.addressLine1} {billing_address.housenumber}</p>
                                <p>{billing_address.postal_code} {billing_address.city}</p>
                                <p>{billing_address.country}</p>
                            </div>
                        </div>

                        <div className="od2-card">
                            <h4 className="od2-card-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>
                                {t("order.ShippingAddress")}
                            </h4>
                            <div className="od2-address-block">
                                <p>{shipping_address.addressLine1} {shipping_address.housenumber}</p>
                                <p>{shipping_address.postal_code} {shipping_address.city}</p>
                                <p>{shipping_address.country}</p>
                            </div>
                        </div>
        </div>
    );
    const payoutHoldDate = order.created_at || delivery_date;
    const canHoldPayout = payoutHoldDate
        ? (Date.now() - new Date(payoutHoldDate).getTime()) / (1000 * 60 * 60 * 24) <= 14
        : false;

    return (
        <>
            <div className="od2-page container">
                <div className="od2-header">
                    <div className="od2-header-left">
                        <div className="od2-order-title">
                            <span className="od2-order-id-label">{t("order.OrderId")}</span>
                            <span className="od2-order-id-number">#{order_id}</span>
                            {order_status && (
                                <span className="d-flex gap-2 align-items-center">
                                    <svg width="7" height="7" viewBox="0 0 10 10"><circle cx="5" cy="5" r="5" fill="currentColor" /></svg>
                                    {is_dispute ? (
                                        <span>{t("order.Dispute")}</span>
                                    ) : (
                                        <StatusText status={order_status} />
                                    )}
                                </span>
                            )}
                        </div>
                        <div className="od2-order-meta">
                            <span className="od2-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                {t("order.OrderPlaced")}: {formatDateTime(order_created_at, i18n)}
                            </span>
                            {delivery_date && (
                                <span className="od2-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 3h15v13H1z" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>
                                    {t("order.Delivered")}: {formatDateTime(delivery_date, i18n)}
                                </span>
                            )}
                            {payment_method && (
                                <span className="od2-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 3h15v13H1z" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>
                                    {t("order.PaymentMethod")}: {payment_method === 'credit_points' ? t("order.CreditPoints") : payment_method}
                                </span>
                            )}
                        </div>
                    </div>

                    {isCancelled && (
                        <div className="od2-header-actions">
                            <span className="od2-status-pill cancelled">
                                <svg width="7" height="7" viewBox="0 0 10 10"><circle cx="5" cy="5" r="5" fill="currentColor" /></svg>
                                {t("order.OrderCancelled")}
                            </span>
                        </div>
                    )}
                </div>

                <div className="od2-layout">
                    <div className="od2-col-left">
                        <div className="od2-card">
                            <h4 className="od2-card-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                                    <line x1="3" y1="6" x2="21" y2="6" />
                                    <path d="M16 10a4 4 0 01-8 0" />
                                </svg>
                                {t("order.OrderItems", { count: purchasedVariants.length })}
                            </h4>

                            {purchasedVariants.map((entry, index) => {
                                const { parentItem, variant } = entry;
                                const itemImage = variant?.images?.[0] || parentItem?.image_urls?.[0] || null;
                                const itemTitle = parentItem?.title || "—";
                                const itemPrice = variant?.bid_amount != 0 ? variant.bid_amount : parentItem?.original_retail_price ?? null;
                                const isLast = index === purchasedVariants.length - 1;
                                const qty = parentItem?.quantity || 1;


                                return (
                                    <div
                                        key={`${parentItem?.id}-${variant?.id}`}
                                        className={`od2-item-row ${!isLast ? "od2-item-row--divider" : ""}`}
                                    >
                                        <img
                                            className="od2-item-img"
                                            src={getFullImageUrl(itemImage)}
                                            alt={itemTitle}
                                        />
                                        <div className="od2-item-body">
                                            {parentItem?.brand_name && (
                                                <p className="od2-item-brand">{parentItem.brand_name}</p>
                                            )}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                                                <h5 className="od2-item-name">{itemTitle}</h5>
                                                <div className="od2-item-attrs">
                                                    {parentItem?.is_dispute && (
                                                        <span className="od2-attr-chip">{t("order.Disputed")}</span>
                                                    )}
                                                    {parentItem?.is_return && (
                                                        <span className="od2-attr-chip">{t("order.Returned")}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="od2-item-attrs">
                                                {variant?.size_name && (
                                                    <span className="od2-attr-chip">
                                                        {t("order.Size")}: <strong>{variant.size_name}</strong>
                                                    </span>
                                                )}
                                                {variant?.color_name && (
                                                    <span className="od2-attr-chip">
                                                        {t("order.Color")}: <strong>
                                                            <TranslatedText text={variant?.color_name} />
                                                        </strong>
                                                    </span>
                                                )}
                                                {itemPrice != null && (
                                                    <span className="od2-attr-chip od2-attr-chip--price">
                                                        {formatPrice(itemPrice, i18n)}
                                                    </span>
                                                )}
                                                {qty && (
                                                    <span className="od2-attr-chip">
                                                        {t("order.Quantity")}: <strong>{qty}</strong>
                                                    </span>
                                                )}
                                            </div>

                                            <div className="od2-actions">
                                                <button className="od2-btn-detail" onClick={() => handleDetail(parentItem?.slug)}>
                                                    {t("order.MoreDetails")}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Order Summary */}
                        <div className="od2-card">
                            <h4 className="od2-card-title mb-0">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
                                {t("order.OrderSummary")}
                            </h4>
                            <div className="od2-summary-rows">
                                <div className="od2-summary-row">
                                    <span className="od2-summary-label">{t("order.Subtotal")}</span>
                                    <span className="od2-summary-value">{formatPrice(amount, i18n)}</span>
                                </div>
                                <div className="od2-summary-row total pb-0">
                                    <span className="od2-summary-label">{t("order.Total")}</span>
                                    <span className="od2-summary-value">{formatPrice(total_amount, i18n)}</span>
                                </div>
                            </div>
                        </div>

                        {canCancelOrder && !showAdminReviewActions && (
                            <div className="d-flex gap-2 mb-3 flex-wrap">
                                <button
                                    className="orange-btn"
                                    onClick={() => setCancelOrderModal(true)}
                                >
                                    Cancel Order
                                </button>
                            </div>
                        )}

                        {showAdminReviewActions && (
                            <div className="d-flex gap-2 mb-3 flex-wrap">
                                <button
                                    className="orange-btn"
                                    onClick={() => setExtendDeadlineModal(true)}
                                >
                                    Extend Seller Shipment Days
                                </button>
                                <button
                                    className="orange-btn"
                                    onClick={() => setCreateShipmentModal(true)}
                                >
                                    Create Shipment
                                </button>
                                <button
                                    className="orange-btn"
                                    onClick={() => setCancelOrderModal(true)}
                                >
                                    Cancel Order
                                </button>
                            </div>
                        )}

                        {isDelivered && (
                            <div className="d-flex gap-2 mb-3 flex-wrap">
                                {!isReturnOrder && (
                                    <button
                                        className="orange-btn"
                                        onClick={() => setRefundConfirmation(true)}
                                    >
                                        Refund Payment
                                    </button>
                                )}

                                {!isPayoutOnHold && canHoldPayout && (
                                    <button
                                        className="orange-btn"
                                        onClick={() => setReasonModal(true)}
                                    >
                                        Seller Payout Hold
                                    </button>
                                )}

                                {isPayoutOnHold && (
                                    <button
                                        className="orange-btn"
                                        onClick={() => setReleaseConfirmation(true)}
                                    >
                                        Release Seller Payout
                                    </button>
                                )}
                            </div>
                        )}

                        {!isHiddenStatus && (
                            <>
                                <div className="od2-card">
                                    <div className="od2-track-header">
                                        <h4 className="od2-card-title" style={{ margin: 0, border: "none", paddingBottom: 0 }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                            {t("order.TrackOrder")}
                                        </h4>
                                    </div>
                                    <StatusTracker
                                        isReturn={!!isReturn}
                                        isRefunded={order.is_refunded}
                                        status={order_status}
                                        hasParcel={!!(parcel_id && String(parcel_id).trim())}
                                        className={isReturn ? "return-flow" : ""}
                                    />
                                </div>

                                <div className="od2-card">
                                    <div className="od2-track-header">
                                        <h4 className="od2-card-title" style={{ margin: 0, border: "none", paddingBottom: 0 }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                            {t("order.DownloadInvoice")}
                                        </h4>
                                    </div>
                                    <div className="d-flex gap-2 flex-wrap">
                                        <button className="od2-btn-action return" onClick={() => downloadInvoice('buyer')} disabled={loading}>
                                            {t("order.DownloadBuyerInvoice")} {loading && downloadType === "buyer" && "…"}
                                        </button>
                                        <button className="od2-btn-action return" onClick={() => downloadInvoice('seller')} disabled={loading}>
                                            {t("order.DownloadSellerInvoice")} {loading && downloadType === "seller" && "…"}
                                        </button>
                                        <button className="od2-btn-action return" onClick={() => downloadInvoice('admin')} disabled={loading}>
                                            {t("order.DownloadCommissionInvoice")} {loading && downloadType === "admin" && "…"}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        {isCancelled && addressCards}
                    </div>

                    <div className="od2-col-right">
                        {sellerdata && (
                            <div className="od2-card">
                                <h4 className="od2-card-title">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                                    {t("order.SellerDetails")}
                                </h4>
                                {/* ... seller content remains same ... */}
                                {sellerdata.promoted && sellerdata.company_logo && (
                                    <img className="od2-seller-logo" src={`${REACT_APP_IMAGE_URL}${sellerdata.company_logo}`} alt="Seller logo" />
                                )}
                                {sellerdata.store_name && <p className="od2-seller-name">{sellerdata.store_name}</p>}
                                <ul className="od2-contact-list">
                                    {sellerdata.user_name && (
                                        <li className="od2-contact-item">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                            {sellerdata.user_name}
                                        </li>
                                    )}
                                    {sellerdata.location && (
                                        <li className="od2-contact-item">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                            {sellerdata.location}
                                        </li>
                                    )}
                                    {sellerdata.email && (
                                        <li className="od2-contact-item">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                                            <a href={`mailto:${sellerdata.email}`}>{sellerdata.email}</a>
                                        </li>
                                    )}
                                    {sellerdata.phone_no && (
                                        <li className="od2-contact-item clickable" onClick={handleContactPhone}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                                            {sellerdata.country_code} {sellerdata.phone_no}
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}

                        <div className="od2-card">
                            <h4 className="od2-card-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                                {t("order.BuyerDetails")}
                            </h4>
                            <div className="od2-info-list">
                                <div className="od2-info-item">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                    <span>{userdata.user_name}</span>
                                </div>
                                <div className="od2-info-item">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                                    <span>{userdata.email}</span>
                                </div>
                                {userdata.phone_no && (
                                    <div className="od2-info-item">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                                        <span>{userdata.country_code} {userdata.phone_no}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {!isCancelled && addressCards}
                    </div>
                </div>

                <section className="od2-card od2-audit-card" aria-labelledby="order-audit-title">
                    <div className="od2-audit-header">
                        <h4 id="order-audit-title" className="od2-card-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="9" />
                                <polyline points="12 7 12 12 16 14" />
                            </svg>
                            {t("order.OrderAuditLog")}
                        </h4>
                        <span className="od2-audit-count">
                            {t("order.AuditEvents", { count: totalOrderAuditLogs || auditLogs.length })}
                        </span>
                    </div>

                    {auditLogs.length ? (
                        <ol className="od2-audit-timeline">
                            {auditLogs.map((log, index) => {
                                const tone = getAuditTone(log);
                                const rawRole = log.role || log.actor_role || log.actor_type;
                                const normalizedRole = rawRole?.toLowerCase();
                                const knownRoles = ["buyer", "seller", "admin", "system"];
                                const role = knownRoles.includes(normalizedRole)
                                    ? t(`order.AuditRoles.${normalizedRole}`)
                                    : humanizeAuditValue(rawRole);
                                const actor = log.user_name || role || t("order.AuditRoles.system");
                                const currency = log.currency || log.metadata?.currency || "";

                                return (
                                    <li className="od2-audit-event" key={log.id || `${log.created_at}-${index}`}>
                                        <span className={`od2-audit-marker ${tone}`} aria-hidden="true">
                                            <span />
                                        </span>
                                        <div className="od2-audit-content">
                                            <div className="od2-audit-event-header">
                                                <div>
                                                    <h5>{log.action_label || humanizeAuditValue(log.action_type) || t("order.OrderUpdated")}</h5>
                                                    <p className="od2-audit-time">{formatDateTime(log.created_at, i18n)}</p>
                                                </div>
                                                <span className={`od2-audit-status ${tone}`}>
                                                    {log.status === true ? t("order.Completed") : t("order.NotCompleted")}
                                                </span>
                                            </div>

                                            {log.message && <p className="od2-audit-message">{log.message}</p>}

                                            <div className="od2-audit-details">
                                                <span><strong>{t("order.Actor")}</strong>{actor}{role && role.toLowerCase() !== actor.toLowerCase() ? ` · ${role}` : ""}</span>
                                                {log.old_status && log.new_status && (
                                                    <span>
                                                        <strong>{t("order.Status")}</strong>
                                                        {humanizeAuditValue(log.old_status)}
                                                        <span className="od2-audit-arrow" aria-hidden="true">→</span>
                                                        {humanizeAuditValue(log.new_status)}
                                                    </span>
                                                )}
                                                {log.amount != null && (
                                                    <span><strong>{t("order.Amount")}</strong>{currency ? `${log.amount} ${currency}` : log.amount}</span>
                                                )}
                                                {log.reference_id && (
                                                    <span className="od2-audit-reference"><strong>{t("order.Reference")}</strong>{log.reference_id}</span>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    ) : (
                        <div className="od2-audit-empty">
                            <span>{t("order.NoAuditEvents")}</span>
                        </div>
                    )}
                </section>
            </div>




            <Modal open={refundConfirmation} onClose={() => setRefundConfirmation(false)}>
                <ModalDialog>
                    <DialogTitle>Refund Confirmation</DialogTitle>
                    <DialogContent>
                        <p className="m-0">Are you sure you want to refund the payment for this order?</p>
                    </DialogContent>
                    <Box sx={{ p: 2 }} className="pb-0">
                        <div className='d-flex gap-2 justify-content-end'>
                            <button className='model-no-btn' onClick={() => setRefundConfirmation(false)}>Cancel</button>
                            <button
                                className={`model-yes-btn`}
                                onClick={handleRefund}
                            > Refund Payment </button>
                        </div>
                    </Box>
                </ModalDialog>
            </Modal>

            <Modal open={releaseConfirmation} onClose={() => setReleaseConfirmation(false)}>
                <ModalDialog>
                    <DialogTitle>Release Payout Confirmation</DialogTitle>
                    <DialogContent>
                        <p className="m-0">Are you sure you want to release the payout for seller?</p>
                    </DialogContent>
                    <Box sx={{ p: 2 }} className='pb-0'>
                        <div className='d-flex gap-2 justify-content-end'>
                            <button className='model-no-btn' onClick={() => setReleaseConfirmation(false)}>Cancel</button>
                            <button
                                className={`model-yes-btn`}
                                onClick={releasePayout}
                            > Release Payout </button>
                        </div>
                    </Box>
                </ModalDialog>
            </Modal>


            <Modal open={reasonModal} onClose={() => setReasonModal(false)}>
                <ModalDialog>
                    <DialogTitle>Seller Payout Block</DialogTitle>
                    <DialogContent>
                        <div className="reject-reason-container">
                            <label htmlFor="reason" className="reject-reason-label">
                                Payout Block Reason *
                            </label>
                            <textarea
                                name="reason"
                                id="reason"
                                className="reject-reason-textarea"
                                placeholder="Please explain your reason..."
                                rows="5"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                    </DialogContent>
                    <Box sx={{ p: 2 }}>
                        <div className='d-flex gap-2 justify-content-end'>
                            <button className='model-no-btn' onClick={() => setReasonModal(false)}>Cancel</button>
                            <button
                                className={`model-yes-btn ${!reason.trim() && 'modal-yes-btn-disable'}`}
                                onClick={holdPayout}
                                disabled={!reason.trim()}
                            >
                                Hold Payout
                            </button>
                        </div>
                    </Box>
                </ModalDialog>
            </Modal>

            <Modal
                open={cancelOrderModal}
                onClose={() => {
                    if (!cancelLoading) closeCancelModal();
                }}
            >
                <ModalDialog className="cancel-order-modal">
                    <DialogTitle className="cancel-order-title">
                        <span className="cancel-order-title-icon"><i className="bi bi-slash-circle" /></span>
                        <span>
                            Cancel Order
                            <small>This action cannot be undone.</small>
                        </span>
                    </DialogTitle>
                    <DialogContent>
                        <div className="cancel-order-field">
                            <label htmlFor="cancelOrderReasonCode" className="cancel-order-label">
                                Reason code <span>*</span>
                            </label>
                            <Select
                                inputId="cancelOrderReasonCode"
                                classNamePrefix="cancel-select"
                                className="cancel-select"
                                isSearchable={false}
                                isDisabled={cancelLoading}
                                placeholder="Select a reason"
                                options={cancelReasonOptions}
                                value={cancelReasonOptions.find((option) => option.value === cancelReasonCode) || null}
                                onChange={(option) => setCancelReasonCode(option?.value || "")}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                components={{
                                    IndicatorSeparator: null,
                                    ValueContainer: CancelSelectValueContainer,
                                }}
                            />
                        </div>

                        <div className="cancel-order-field">
                            <label htmlFor="cancelOrderReason" className="cancel-order-label">
                                Cancellation note <span>*</span>
                            </label>
                            <textarea
                                id="cancelOrderReason"
                                className="cancel-order-textarea"
                                placeholder="Describe why this order is being cancelled..."
                                rows="3"
                                value={cancelReason}
                                disabled={cancelLoading}
                                onChange={(event) => setCancelReason(event.target.value)}
                            />
                        </div>

                        <div className="cancel-order-field">
                            <span className="cancel-order-label">
                                How should the buyer be refunded? <span>*</span>
                            </span>
                            <div className="cancel-order-refund-options" role="radiogroup">
                                {CANCEL_REFUND_OPTIONS.map((option) => (
                                    <label
                                        key={option.value}
                                        className={`cancel-order-refund-card${refundMethod === option.value ? " selected" : ""}`}
                                    >
                                        <span className="cancel-order-icon-tile"><i className={`bi ${option.icon}`} /></span>
                                        <span className="cancel-order-refund-text">
                                            <strong>{option.title}</strong>
                                            <small>{option.description}</small>
                                        </span>
                                        <input
                                            type="radio"
                                            name="cancelRefundMethod"
                                            value={option.value}
                                            checked={refundMethod === option.value}
                                            disabled={cancelLoading}
                                            onChange={() => setRefundMethod(option.value)}
                                        />
                                    </label>
                                ))}
                            </div>
                        </div>
                    </DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <div className="d-flex gap-2 justify-content-end">
                            <button
                                className="model-no-btn"
                                disabled={cancelLoading}
                                onClick={closeCancelModal}
                            >
                                Keep Order
                            </button>
                            <button
                                className={`model-yes-btn ${!canSubmitCancel ? "modal-yes-btn-disable" : ""}`}
                                disabled={!canSubmitCancel || cancelLoading}
                                onClick={handleCancelOrder}
                            >
                                {cancelLoading ? "Cancelling..." : "Cancel Order"}
                            </button>
                        </div>
                    </Box>
                </ModalDialog>
            </Modal>

            <Modal
                open={extendDeadlineModal}
                onClose={() => {
                    if (!extendLoading) setExtendDeadlineModal(false);
                }}
            >
                <ModalDialog>
                    <DialogTitle>Extend Seller Shipment Deadline</DialogTitle>
                    <DialogContent>
                        <div className="reject-reason-container">
                            <label htmlFor="extensionWorkingDays" className="reject-reason-label">
                                Extension (working days) *
                            </label>
                            <input
                                id="extensionWorkingDays"
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                className="form-control"
                                placeholder="1 or 2"
                                value={extensionWorkingDays}
                                disabled={extendLoading}
                                onChange={(event) => {
                                    const digits = event.target.value.replace(/\D/g, "");
                                    setExtensionWorkingDays(digits && Number(digits) <= MAX_EXTENSION_DAYS && Number(digits) >= 1 ? digits : "");
                                }}
                            />
                            <small className="text-muted">Maximum {MAX_EXTENSION_DAYS} working days.</small>
                        </div>

                        <div className="reject-reason-container">
                            <label htmlFor="extendDeadlineReason" className="reject-reason-label">
                                Reason *
                            </label>
                            <textarea
                                id="extendDeadlineReason"
                                className="reject-reason-textarea"
                                placeholder="Please enter the reason for extending the shipment deadline..."
                                rows="5"
                                value={extendReason}
                                disabled={extendLoading}
                                onChange={(event) => setExtendReason(event.target.value)}
                            />
                        </div>
                    </DialogContent>
                    <Box sx={{ p: 2 }}>
                        <div className="d-flex gap-2 justify-content-end">
                            <button
                                className="model-no-btn"
                                disabled={extendLoading}
                                onClick={() => {
                                    setExtendDeadlineModal(false);
                                    setExtendReason("");
                                    setExtensionWorkingDays("");
                                }}
                            >
                                Close
                            </button>
                            <button
                                className={`model-yes-btn ${(!extendReason.trim() || !Number(extensionWorkingDays) || Number(extensionWorkingDays) <= 0) ? "modal-yes-btn-disable" : ""}`}
                                disabled={!extendReason.trim() || !Number(extensionWorkingDays) || Number(extensionWorkingDays) <= 0 || extendLoading}
                                onClick={handleExtendDeadline}
                            >
                                {extendLoading ? "Extending..." : "Extend Deadline"}
                            </button>
                        </div>
                    </Box>
                </ModalDialog>
            </Modal>

            <Modal
                open={createShipmentModal}
                onClose={handleCloseCreateShipment}
            >
                <ModalDialog className={`shipment-modal${packageDetailsConfirmed && shippingMethodsLoaded ? " shipment-modal--expanded" : ""}`}>
                    <DialogTitle>Create Shipment</DialogTitle>
                    <DialogContent>
                        {!packageDetailsConfirmed ? (
                            <>
                                <p className="shipment-modal-hint">
                                    Enter the parcel dimensions and weight to get accurate shipping options.
                                </p>
                                <div className="package-details-box">
                                    <div className="row g-3">
                                        <div className="col-6 col-md-3">
                                            <label className="mb-2">Length *</label>
                                            <div className="input-group">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    className={`form-control${packageDetailsErrors.length ? " is-invalid" : ""}`}
                                                    placeholder="30"
                                                    value={packageDetails.length}
                                                    onChange={(event) => handlePackageDetailChange("length", event.target.value)}
                                                    onWheel={(event) => event.target.blur()}
                                                />
                                                <span className="input-group-text">cm</span>
                                            </div>
                                            {packageDetailsErrors.length && (
                                                <div className="error_msg">Required</div>
                                            )}
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <label className="mb-2">Width *</label>
                                            <div className="input-group">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    className={`form-control${packageDetailsErrors.width ? " is-invalid" : ""}`}
                                                    placeholder="20"
                                                    value={packageDetails.width}
                                                    onChange={(event) => handlePackageDetailChange("width", event.target.value)}
                                                    onWheel={(event) => event.target.blur()}
                                                />
                                                <span className="input-group-text">cm</span>
                                            </div>
                                            {packageDetailsErrors.width && (
                                                <div className="error_msg">Required</div>
                                            )}
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <label className="mb-2">Height *</label>
                                            <div className="input-group">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    className={`form-control${packageDetailsErrors.height ? " is-invalid" : ""}`}
                                                    placeholder="15"
                                                    value={packageDetails.height}
                                                    onChange={(event) => handlePackageDetailChange("height", event.target.value)}
                                                    onWheel={(event) => event.target.blur()}
                                                />
                                                <span className="input-group-text">cm</span>
                                            </div>
                                            {packageDetailsErrors.height && (
                                                <div className="error_msg">Required</div>
                                            )}
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <label className="mb-2">Weight *</label>
                                            <div className="input-group">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    className={`form-control${packageDetailsErrors.weight ? " is-invalid" : ""}`}
                                                    placeholder="1.5"
                                                    value={packageDetails.weight}
                                                    onChange={(event) => handlePackageDetailChange("weight", event.target.value)}
                                                    onWheel={(event) => event.target.blur()}
                                                />
                                                <span className="input-group-text">kg</span>
                                            </div>
                                            {packageDetailsErrors.weight && (
                                                <div className="error_msg">Required</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="package-details-box mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="shipping-option-meta" style={{ margin: 0 }}>
                                            <span>{Number(packageDetails.length)}×{Number(packageDetails.width)}×{Number(packageDetails.height)} cm</span>
                                            <span>{Number(packageDetails.weight)} kg</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="od2-btn-detail"
                                            onClick={() => setPackageDetailsConfirmed(false)}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </div>

                                {shippingMethodsLoading ? (
                                    <div className="d-flex align-items-center justify-content-center py-4">
                                        <div className="spinner-border text-orange" role="status" style={{ width: "2rem", height: "2rem" }}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span className="ms-3">Loading shipping methods…</span>
                                    </div>
                                ) : shippingMethodsLoaded && shippingOptions.length > 0 ? (
                                    <div className="reject-reason-container">
                                        <label className="reject-reason-label">Shipping Method *</label>
                                        <Select
                                            options={shippingOptions}
                                            value={selectedShippingMethod}
                                            onChange={(selected) => setSelectedShippingMethod(selected)}
                                            placeholder="Choose shipping method…"
                                            isSearchable
                                            classNamePrefix="shipping-select"
                                            formatOptionLabel={(option, { context }) => (
                                                context === "value" ? (
                                                    <div className="selected-shipping-method">
                                                        <div className="selected-shipping-content">
                                                            <div className="selected-shipping-title">{option.label}</div>
                                                            <div className="selected-shipping-meta">
                                                                <span>{option.carrier}</span>
                                                                <span>{option.minWeight}kg – {option.maxWeight}kg</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="shipping-option-card">
                                                        <div className="shipping-option-title">{option.label}</div>
                                                        <div className="shipping-option-meta">
                                                            <span>{option.carrier}</span>
                                                            <span>{option.minWeight}kg – {option.maxWeight}kg</span>
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        />
                                    </div>
                                ) : shippingMethodsLoaded ? (
                                    <div className="alert alert-warning">No shipping methods available for this package.</div>
                                ) : null}

                                {shippingMethodsLoaded && (
                                    <div className="reject-reason-container">
                                        <label htmlFor="adminSupportReason" className="reject-reason-label">
                                            Reason *
                                        </label>
                                        <textarea
                                            id="adminSupportReason"
                                            className="reject-reason-textarea"
                                            placeholder="Please enter the reason for creating this shipment on the seller's behalf..."
                                            rows="4"
                                            value={adminSupportReason}
                                            disabled={shipmentLoading}
                                            onChange={(event) => setAdminSupportReason(event.target.value)}
                                        />
                                    </div>
                                )}

                                {shippingMethodsLoaded && (
                                    <div className="reject-reason-container">
                                        <label className="d-flex align-items-center gap-2" style={{ cursor: shipmentLoading ? "default" : "pointer" }}>
                                            <input
                                                type="checkbox"
                                                checked={sellerConfirmedShipReady}
                                                disabled={shipmentLoading}
                                                onChange={(event) => setSellerConfirmedShipReady(event.target.checked)}
                                            />
                                            Seller confirmed the item is ready to ship
                                        </label>
                                    </div>
                                )}
                            </>
                        )}
                    </DialogContent>
                    <Box sx={{ p: 2 }}>
                        <div className="d-flex gap-2 justify-content-end">
                            <button
                                className="model-no-btn"
                                disabled={shipmentLoading}
                                onClick={handleCloseCreateShipment}
                            >
                                Close
                            </button>
                            {!packageDetailsConfirmed ? (
                                <button
                                    className="model-yes-btn"
                                    onClick={handleConfirmPackageDetails}
                                >
                                    Continue to Shipping
                                </button>
                            ) : (
                                <button
                                    className={`model-yes-btn ${(!selectedShippingMethod || !adminSupportReason.trim()) ? "modal-yes-btn-disable" : ""}`}
                                    disabled={!selectedShippingMethod || !adminSupportReason.trim() || shipmentLoading}
                                    onClick={handleCreateShipment}
                                >
                                    {shipmentLoading ? "Creating..." : "Create Shipment"}
                                </button>
                            )}
                        </div>
                    </Box>
                </ModalDialog>
            </Modal>

        </>
    );
};

export default OrderDetails;
